/* BattleIntro — cinematic pre-battle sequence.
 *
 *   1. Dark screen + WildQuest logo (300ms)
 *   2. "RIVAL BATTLE" title zooms in (500ms)
 *   3. Challenger profile slides in from the LEFT (700ms)
 *   4. Opponent profile slides in from the RIGHT (700ms, overlaps)
 *   5. "VS" impact — screen shake + particle burst + audio cue (900ms)
 *   6. Fade to arena reveal (400ms)
 *
 * Total ~3.5s; skippable at any time via the SKIP pill top-right.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Crown, Sparkles } from 'lucide-react';
import WildQuestLogo from '../WildQuestLogo';

const PHASES = ['logo', 'title', 'sides', 'vs', 'done'];
const PHASE_DURS = { logo: 350, title: 500, sides: 900, vs: 1000, done: 0 };

function IntroSide({ side, name, rank, tier, avatar, cardPhoto, cardName, cardRarity }) {
  const dirClass = side === 'left' ? 'items-start text-left' : 'items-end text-right';
  return (
    <motion.div
      initial={{ x: side === 'left' ? -140 : 140, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className={`flex flex-col ${dirClass} gap-2`}
      data-testid={`intro-${side === 'left' ? 'challenger' : 'opponent'}`}
    >
      <div className={`flex ${side === 'left' ? 'flex-row' : 'flex-row-reverse'} items-center gap-2`}>
        <img
          src={avatar}
          alt=""
          onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          className="w-12 h-12 rounded-full object-cover border-2 border-white/25"
        />
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80">
            {side === 'left' ? 'CHALLENGER' : 'OPPONENT'}
          </p>
          <p className="font-serif text-lg leading-tight">{name}</p>
          <p className="text-[10px] text-white/60 flex items-center gap-1">
            <Compass className="w-2.5 h-2.5" />{rank}{tier ? ` · ${tier}` : ''}
          </p>
        </div>
      </div>
      <div className={`relative w-32 aspect-[4/5] rounded-2xl overflow-hidden border-2 border-white/20 shadow-xl wq-intro-card`}>
        {cardPhoto ? (
          <img src={cardPhoto} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-emerald-700 to-teal-900 flex items-center justify-center">
            <span className="font-serif text-white/30 text-4xl">?</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-1.5">
          <p className="text-[9px] uppercase tracking-widest text-white/60">{cardRarity}</p>
          <p className="font-serif text-sm leading-tight truncate">{cardName}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function BattleIntro({ battle, challenger, opponent, onDone, onSkip }) {
  const [phase, setPhase] = useState('logo');

  useEffect(() => {
    let dead = false;
    const advance = (i) => {
      if (dead) return;
      const p = PHASES[i];
      setPhase(p);
      if (p === 'done') { onDone?.(); return; }
      setTimeout(() => advance(i + 1), PHASE_DURS[p]);
    };
    advance(0);
    return () => { dead = true; };
  }, [onDone]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black flex items-center justify-center overflow-hidden"
      data-testid="battle-intro"
    >
      <button
        type="button"
        onClick={onSkip}
        data-testid="battle-intro-skip"
        className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-[10px] uppercase tracking-widest z-10"
      >
        Skip
      </button>

      {/* Ambient particles */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        {Array.from({ length: 20 }).map((_, i) => (
          <span
            key={i}
            className="absolute w-1 h-1 rounded-full bg-emerald-400/60"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `wq-pulse ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite`,
            }}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {phase === 'logo' && (
          <motion.div key="logo"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.4, opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <WildQuestLogo className="w-24 h-24" />
          </motion.div>
        )}

        {phase === 'title' && (
          <motion.div key="title"
            initial={{ scale: 0.7, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 1.1, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
          >
            <p className="text-[10px] uppercase tracking-[0.4em] text-rose-300/80">Rival</p>
            <h1 className="wq-intro-title font-serif text-6xl leading-none tracking-tight bg-gradient-to-b from-white via-white/85 to-rose-100/60 bg-clip-text text-transparent">
              BATTLE
            </h1>
          </motion.div>
        )}

        {(phase === 'sides' || phase === 'vs') && (
          <motion.div key="sides" className="wq-intro-sides relative w-full max-w-4xl px-6 flex items-center justify-between gap-4">
            <IntroSide side="left" {...challenger} />
            <IntroSide side="right" {...opponent} />

            {phase === 'vs' && (
              <motion.div
                initial={{ scale: 0.2, opacity: 0, rotate: -8 }}
                animate={{ scale: [0.2, 1.4, 1], opacity: 1, rotate: [-8, 4, 0] }}
                transition={{ duration: 0.55, times: [0, 0.5, 1] }}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              >
                <div className="relative">
                  <div className="absolute inset-0 blur-2xl bg-rose-500/60 rounded-full" />
                  <p className="wq-intro-vs relative font-serif text-7xl leading-none tracking-tight text-white drop-shadow-[0_0_25px_rgba(251,113,133,0.9)]">
                    VS
                  </p>
                </div>
                {/* Impact particles */}
                {Array.from({ length: 14 }).map((_, i) => (
                  <motion.span
                    key={i}
                    initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
                    animate={{
                      scale: [0, 1, 0],
                      x: Math.cos((i / 14) * Math.PI * 2) * 90,
                      y: Math.sin((i / 14) * Math.PI * 2) * 90,
                      opacity: [1, 1, 0],
                    }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="absolute left-1/2 top-1/2 w-2 h-2 -ml-1 -mt-1 rounded-full bg-gradient-to-r from-rose-400 to-amber-300"
                  />
                ))}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
